import CONFIG from '../config/config';
import { instanceWithV2 as instance } from './SimpleAPIClient';

export type StripeOnboardingResponse = {
  onboardingUrl: string;
};

export type StripeConnectionStatusResponse = {
  connected: boolean;
  reason?: string;
  details?: string;
};

export type StripeReturnResponse = {
  success: boolean;
  message?: string;
};

export type StripeAccountDetails = {
  id: string;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  createdAt: string;
  updatedAt: string;
};

export type StripeOnboardingRequest = {
  businessName?: string;
  redirectUrl?: string;
};

export type StripeAccountUpdateRequest = {
  businessName?: string;
  email?: string;
  phone?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
};

export type StripeAccountUpdateResponse = {
  success: boolean;
  message?: string;
};

export type StripeDashboardLinkResponse = {
  url: string;
  expiresAt: number;
};

export type PaymentMethodInfo = {
  enabled: boolean;
  pmcId: string;
};

export type PaymentMethodsMappingResponse = {
  data: Record<string, PaymentMethodInfo>;
};

export type PaymentMethodPreferenceRequest = {
  preference: 'on' | 'off' | 'none';
};

export type PaymentMethodPreferenceResponse = {
  success: boolean;
  methodKey?: string;
  preference?: string;
  error?: string;
};

export type StripeMonthlyReportingResponse = {
  year: number;
  month: number;
  totalApplicationFeesCents: number;
  currency: string;
};

export type VatStatusResponse = {
  hasVatConfigured: boolean;
};

const PaymentsAPI = {
  
  getConnectionStatus: () => 
    instance(CONFIG.API_ENDPOINT).get<StripeConnectionStatusResponse>('v1/payments/stripe/status'),

  /**
   * Start the Stripe Connect onboarding process
   * @param data Optional data to customize the onboarding experience
   */
  startOnboarding: (data?: StripeOnboardingRequest) => 
    instance(CONFIG.API_ENDPOINT).post<StripeOnboardingResponse>('v1/payments/stripe/onboard', data || {}),

  /**
   * Complete the onboarding process after returning from Stripe
   */
  completeOnboarding: () => 
    instance(CONFIG.API_ENDPOINT).get<StripeReturnResponse>('v1/payments/stripe/return'),

  /**
   * Get detailed information about the connected Stripe account
   */
  getAccountDetails: () => 
    instance(CONFIG.API_ENDPOINT).get<StripeAccountDetails>('v1/payments/stripe/account'),

  /**
   * Get a login link to access the Stripe dashboard
   */
  getDashboardLink: () => 
    instance(CONFIG.API_ENDPOINT).get<StripeDashboardLinkResponse>('v1/payments/stripe/dashboard-link'),

  /**
   * Disconnect the Stripe account (if needed)
   */
  disconnectAccount: () => 
    instance(CONFIG.API_ENDPOINT).delete<StripeReturnResponse>('v1/payments/stripe/disconnect'),

  /**
   * Create a link to update the connected Stripe account information
   */
  createUpdateLink: () => 
    instance(CONFIG.API_ENDPOINT).post<StripeOnboardingResponse>('v1/payments/stripe/update-link'),

  /**
   * Get payment method configurations for the connected account
   */
  getPaymentMethodConfigurations: () => 
    instance(CONFIG.API_ENDPOINT).get<PaymentMethodsMappingResponse>('v1/payments/stripe/pmc'),

  /**
   * Update payment method preference for a specific configuration
   */
  updatePaymentMethodPreference: (pmcId: string, methodKey: string, preference: PaymentMethodPreferenceRequest) => 
    instance(CONFIG.API_ENDPOINT).post<PaymentMethodPreferenceResponse>(`v1/payments/stripe/pmc/${pmcId}/methods/${methodKey}`, preference),

  /**
   * Get monthly platform commission (application fees) in cents
   */
  getMonthlyReporting: (year: number, month: number) =>
    instance(CONFIG.API_ENDPOINT).get<StripeMonthlyReportingResponse>(`v1/payments/stripe/reporting`, {
      params: { year, month }
    }),

  /**
   * Check if VAT is configured for the tenant
   */
  getVatStatus: () =>
    instance(CONFIG.API_ENDPOINT).get<VatStatusResponse>('v1/payments/stripe/vat-status'),

  /**
   * Download invoice PDF for given month/year
   */
  downloadInvoicePDF: async (year: number, month: number) => {
    const response = await instance(CONFIG.API_ENDPOINT).get(
      `v1/payments/stripe/invoice`,
      {
        params: { year, month },
        responseType: 'blob'
      }
    );
    
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    const dispo = (response.headers as any)?.['content-disposition'] || (response.headers as any)?.['Content-Disposition'];
    const match = typeof dispo === 'string' ? dispo.match(/filename="([^"\\]+)"/) : null;
    const filename = match?.[1] || 'invoice.pdf';
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};

export default PaymentsAPI; 